// src/pages/shop/ShopCounterparties.jsx
//
// Список контрагентов магазина: поставщики и оптовые клиенты.
// Розничные покупатели здесь НЕ показываются — они отдельная сущность.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { counterparties as cpApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useDeletePin } from '../../hooks/useDeletePin';
import { formatMoney } from '../../data/currencies';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import SafeInput from '../../components/SafeInput';
import CounterpartyEditSheet from '../../components/CounterpartyEditSheet';
import { useShopNav } from './ShopHome';

const API_HOST = (() => {
  try {
    const u = new URL(process.env.REACT_APP_API_URL || 'https://api.alsatuu.biz/api');
    return `${u.protocol}//${u.host}`;
  } catch { return 'https://api.alsatuu.biz'; }
})();
function fullUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return API_HOST + u;
}

export default function ShopCounterparties() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navItems = useShopNav();
  const { show, ToastView } = useToast();
  const { confirm: confirmDelete, PinModal } = useDeletePin();

  const [filter, setFilter] = useState('all');           // 'all' | 'supplier' | 'wholesale' | 'debts'
  const [search, setSearch] = useState('');
  const [items, setItems]   = useState([]);
  const [debtTotals, setDebtTotals] = useState({ totalShopDebt: 0, totalClientDebt: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);          // null | {} (новый) | {existing}
  // Входящие запросы на связь: «магазин X добавил вас как поставщика»
  const [linkReqs, setLinkReqs] = useState([]);
  const [linkBusy, setLinkBusy] = useState(null);
  // Платежи, которые заявила вторая сторона и ждут моего подтверждения.
  // Деньги двусторонние: «он отправил» ≠ «я получил».
  const [payReqs, setPayReqs] = useState([]);
  const [payBusy, setPayBusy] = useState(null);

  const currency = useMyCurrency();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (filter === 'debts') {
        const r = await cpApi.debts();
        // Фильтруем по поиску локально
        const q = search.trim().toLowerCase();
        let list = r.items || [];
        if (q) {
          list = list.filter(x =>
            x.name.toLowerCase().includes(q) ||
            (x.phone && x.phone.toLowerCase().includes(q))
          );
        }
        setItems(list);
        setDebtTotals({
          totalShopDebt:   r.totalShopDebt   || 0,
          totalClientDebt: r.totalClientDebt || 0,
        });
      } else {
        const r = await cpApi.list({
          type: filter,
          search: search.trim() || undefined,
        });
        setItems(r.items || []);
        setDebtTotals({ totalShopDebt: 0, totalClientDebt: 0 });
      }
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, search, show, t]);

  const loadLinkRequests = useCallback(async () => {
    try {
      const r = await cpApi.linkRequests();
      setLinkReqs(r.items || []);
    } catch (e) {
      // не критично — просто не покажем баннер
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const loadPaymentRequests = useCallback(async () => {
    try {
      const r = await cpApi.paymentsPending();
      setPayReqs(r.items || []);
    } catch (e) { /* не критично */ }
  }, []);

  useEffect(() => { loadLinkRequests(); }, [loadLinkRequests]);
  useEffect(() => { loadPaymentRequests(); }, [loadPaymentRequests]);

  const respondPayment = async (pay, confirm) => {
    setPayBusy(pay.id);
    try {
      if (confirm) {
        await cpApi.paymentConfirm(pay.id);
        show(t('payReq.confirmed'));
      } else {
        await cpApi.paymentDecline(pay.id);
        show(t('payReq.declined'));
      }
      await loadPaymentRequests();
      await load();
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    }
    setPayBusy(null);
  };

  // Подтверждение связи включает синхронизацию документов между магазинами
  const respondLink = async (cp, accept) => {
    setLinkBusy(cp.id);
    try {
      if (accept) {
        await cpApi.linkAccept(cp.id);
        show(t('link.accepted', { name: cp.linkedShop?.name || cp.name }));
      } else {
        await cpApi.linkDecline(cp.id);
        show(t('link.declined'));
      }
      await loadLinkRequests();
      await load();
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    }
    setLinkBusy(null);
  };

  const onSaved = (item, isNew) => {
    setEditing(null);
    show(isNew ? t('counterparties.created') : t('counterparties.updated'));
    load();
  };

  const onDelete = async (cp) => {
    const ok = await confirmDelete({
      title: t('counterparties.deleteTitle', 'Удалить контрагента?'),
      message: t('counterparties.confirmDelete', { name: cp.name }),
    });
    if (!ok) return;
    try {
      await cpApi.remove(cp.id);
      show(t('counterparties.deleted'));
      load();
    } catch (e) {
      show(e.message || t('common.error'), 'error');
    }
  };

  return (
    <div className="page">
      {ToastView}
      {PinModal}

      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('counterparties.subtitle')}</p>
          <h1 className="page-title">{t('counterparties.title')}</h1>
        </div>
        <button
          onClick={() => setEditing({})}
          className="btn btn-primary btn-sm"
        >
          <Icon name="plus" size={14} />
          <span style={{ marginLeft: 4 }}>{t('common.add')}</span>
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        {/* Табы */}
        <div style={{
          display: 'flex', gap: 4, padding: 4,
          background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)',
          marginBottom: 12,
        }}>
          {[
            // Контрагенты — это ТОЛЬКО B2B: поставщики и оптовики.
            // Розничные клиенты живут в своём разделе «Клиенты» — одна сущность, один экран.
            { val: 'all',       label: t('counterparties.tabAll') },
            { val: 'supplier',  label: t('counterparties.tabSuppliers') },
            { val: 'wholesale', label: t('counterparties.tabWholesale') },
            { val: 'debts',     label: t('counterparties.tabDebts') },
          ].map(tab => (
            <button
              key={tab.val}
              onClick={() => setFilter(tab.val)}
              style={{
                flex: 1, padding: '8px 4px',
                background: filter === tab.val ? 'var(--bg-elevated)' : 'transparent',
                border: 'none', borderRadius: 'var(--r-xs)',
                color: filter === tab.val ? 'var(--fg)' : 'var(--fg-muted)',
                fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--font-display)',
                boxShadow: filter === tab.val ? 'var(--shadow-xs)' : 'none',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Сводка долгов — только во вкладке Долги */}
        {filter === 'debts' && (debtTotals.totalShopDebt > 0 || debtTotals.totalClientDebt > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div style={{
              padding: 12,
              background: 'var(--danger-soft)',
              borderRadius: 'var(--r-sm)',
            }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--danger)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('counterparties.debtsYouOwe')}
              </p>
              <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--danger)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(debtTotals.totalShopDebt, currency)}
              </p>
            </div>
            <div style={{
              padding: 12,
              background: 'var(--success-soft)',
              borderRadius: 'var(--r-sm)',
            }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('counterparties.debtsOweYou')}
              </p>
              <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--success)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(debtTotals.totalClientDebt, currency)}
              </p>
            </div>
          </div>
        )}

        {/* Поиск */}
        <div style={{ marginBottom: 16 }}>
          <SafeInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('counterparties.searchPh')}
          />
        </div>

        {loading && (
          <div className="loader" style={{ padding: 32 }}>
            <div className="spinner" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="empty-state">
            <div className="icon">
              <Icon name="users" size={28} stroke={1.5} />
            </div>
            <p>
              {filter === 'debts'
                ? t('counterparties.debtsAllClear')
                : (search ? t('counterparties.emptySearch') : t('counterparties.emptyAll'))}
            </p>
            {!search && filter !== 'debts' && (
              <button
                onClick={() => setEditing({})}
                className="btn btn-primary"
                style={{ marginTop: 16 }}
              >
                <Icon name="plus" size={14} />
                <span style={{ marginLeft: 4 }}>{t('counterparties.addNew')}</span>
              </button>
            )}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Входящие запросы на связь между магазинами */}
            {linkReqs.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {linkReqs.map(req => (
                  <div key={req.id} className="card" style={{
                    padding: 14, marginBottom: 8,
                    border: '1px solid var(--brand-primary)',
                    background: 'var(--brand-primary-soft)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 'var(--r-sm)', flexShrink: 0,
                        background: 'var(--brand-primary)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="globe" size={18} stroke={2} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700 }}>
                          {req.linkedShop?.name || req.name}
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.4 }}>
                          {req.isSupplier ? t('link.wantsToBeCustomer') : t('link.wantsToBeSupplier')}
                        </p>
                      </div>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 10, lineHeight: 1.4 }}>
                      {t('link.explain')}
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        disabled={linkBusy === req.id}
                        onClick={() => respondLink(req, true)}
                      >
                        {t('link.accept')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ flex: 1 }}
                        disabled={linkBusy === req.id}
                        onClick={() => respondLink(req, false)}
                      >
                        {t('link.decline')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Платежи, ждущие моего подтверждения */}
            {payReqs.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {payReqs.map(pay => (
                  <div key={pay.id} className="card" style={{
                    padding: 14, marginBottom: 8,
                    border: '1px solid var(--warning, #F59E0B)',
                    background: 'var(--warning-soft, #FEF3C7)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 'var(--r-sm)', flexShrink: 0,
                        background: 'var(--warning, #F59E0B)', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name="wallet" size={18} stroke={2} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700 }}>
                          {formatMoney(pay.amount, currency)}
                        </p>
                        <p style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.4 }}>
                          {pay.counterparty?.name} · {t(pay.direction === 'incoming' ? 'payReq.theyPaid' : 'payReq.theyReceived')}
                        </p>
                      </div>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--fg-muted)', marginBottom: 10, lineHeight: 1.4 }}>
                      {t('payReq.explain')}
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        disabled={payBusy === pay.id}
                        onClick={() => respondPayment(pay, true)}
                      >
                        {t('payReq.confirm')}
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ flex: 1 }}
                        disabled={payBusy === pay.id}
                        onClick={() => respondPayment(pay, false)}
                      >
                        {t('payReq.decline')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {items.map(cp => (
              <div
                key={cp.id}
                onClick={() => navigate(`/counterparty/${cp.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 14px', borderRadius: 'var(--r-sm)',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  cursor: 'pointer',
                }}
              >
                {cp.linkedShop?.photo ? (
                  <img
                    src={fullUrl(cp.linkedShop.photo)}
                    alt=""
                    style={{
                      width: 40, height: 40,
                      borderRadius: 'var(--r-sm)',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 40, height: 40,
                    borderRadius: 'var(--r-sm)',
                    background: cp.isSupplier ? 'var(--brand-primary-soft)' : 'var(--success-soft)',
                    color:      cp.isSupplier ? 'var(--brand-primary)'    : 'var(--success)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon name={cp.isSupplier ? 'truck' : 'cart'} size={18} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cp.name || '—'}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[
                      cp.isSupplier && t('counterparties.badgeSupplier'),
                      cp.isWholesaleCustomer && t('counterparties.badgeWholesale'),
                    ].filter(Boolean).join(' · ')}
                  </p>
                  {(cp.phone || cp.whatsapp) && (
                    <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)' }}>
                      {cp.phone || cp.whatsapp}
                    </p>
                  )}
                </div>
                {filter === 'debts' ? (
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    {cp.shopDebt > 0 && (
                      <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--danger)', fontVariantNumeric: 'tabular-nums' }}>
                        −{formatMoney(cp.shopDebt, currency)}
                      </p>
                    )}
                    {cp.clientDebt > 0 && (
                      <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>
                        +{formatMoney(cp.clientDebt, currency)}
                      </p>
                    )}
                  </div>
                ) : (
                  <Icon name="chevronRight" size={16} color="var(--fg-faint)" style={{ flexShrink: 0 }} />
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ height: 40 }} />
      </div>

      <CounterpartyEditSheet
        open={!!editing}
        counterparty={editing && editing.id ? editing : null}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />

      <BottomNav items={navItems} />
    </div>
  );
}

const badgeStyle = {
  fontSize: 10, fontWeight: 700,
  color: 'var(--brand-primary)',
  background: 'var(--brand-primary-soft)',
  padding: '2px 6px', borderRadius: 4,
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const badgeStyle2 = {
  ...badgeStyle,
  color: 'var(--success)',
  background: 'var(--success-soft)',
};
